<template>
  <div>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button variant="primary" to="/journalentry/add">
            <feather-icon icon="PlusIcon" class="" />
            Add New
          </b-button>
        </div>
      </b-col>
      <b-col v-if="loading" cols="12" class="text-center my-2">
        <b-spinner variant="success" label="Spinning" block style="width: 3rem; height: 3rem"></b-spinner>
      </b-col>
      <template v-if="!loading">
        <b-col cols="12" md="2" sm="2" class="my-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">Per page</label>
            <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions" />
          </b-form-group>
        </b-col>

        <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
          <b-button variant="outline-danger"  @click="clearFilters">
            Clear Filters
          </b-button>
        </b-col>

        <b-col cols="12">
          <b-table responsive :current-page="currentPage" :per-page="perPage" :items="filteredJournalEntries"  :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc" :fields="fields" class="" striped>

            <template #head(ref)="scope">
              <div class="d-flex flex-column">
                <span>{{ scope.label }}</span>
                <b-form-input v-model="filters.refID" placeholder="Search Reference" size="sm" />
              </div>
            </template>
            <template #head(date)="scope">
              <div class="d-flex flex-column">
                <span>{{ scope.label }}</span>
                <DatePicker v-model="filters.dateRange" range placeholder="Select Date Range" size="sm"
                  class="small-datepicker" />
              </div>
            </template>
            <template #head(debit)="scope">
              <div class="d-flex flex-column">
                <span>{{ scope.label }}</span>
                <b-form-input v-model="filters.debit" placeholder="Search Debit" size="sm" type="number" />
              </div>
            </template>
            <template #head(credit)="scope">
              <div class="d-flex flex-column">
                <span>{{ scope.label }}</span>
                <b-form-input v-model="filters.credit" placeholder="Search Credit" size="sm" type="number" />
              </div>
            </template>

            <template v-slot:cell(ref)="row">
              <span>{{ row.item.refID }}</span>
            </template>
            <template v-slot:cell(date)="row">
              <span>{{ formatDate(row.item.date) }}</span>
            </template>
            <template v-slot:cell(debit)="row">
              <span>{{ row.item.totalDebit }}</span>
            </template>
            <template v-slot:cell(credit)="row">
              <span>{{ row.item.totalCredit }}</span>
            </template>
            <template v-slot:cell(actions)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
                </template>

                <b-dropdown-item :to="viewJournalEntry(row.item.id)">
                  <feather-icon icon="EyeIcon" class="mr-50" />
                  <span>View</span>
                </b-dropdown-item>
                <b-dropdown-item :to="{path : `/journalentry/edit/${row.item.id}`}">
                  <feather-icon icon="Edit2Icon" class="mr-50" />
                  <span>Edit</span>
                </b-dropdown-item>

                <b-dropdown-item @click="deleteEntry(row.item.id)">
                  <feather-icon icon="TrashIcon" class="mr-50" />
                  <span>Delete</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </b-col>

        <b-col cols="12">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
            class="my-0" />
        </b-col>
      </template>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BTable,
  BButton,
  BFormInput,
  BInputGroupAppend,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormSelect,
  BInputGroup,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { customError, customLoader, customSwal } from "@/libs/customFn";
export default {
  components: {
    BRow,
    BCol,
    BTable,
    BButton,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    BSpinner,
    DatePicker
  },
  data() {
    return {
      fields: [
      { key: "ref", label: "Reference", sortable: true },
      { key: "date", label: "Date", sortable: true },
      { key: "debit", label: "Debit", sortable: true },
      { key: "credit", label: "Credit", sortable: true },
      { key: "actions", label: "Actions" }, // Not sortable
    ],
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 1,
      filter: "",
      loading: true,
      sortBy: "date", // Default sorting column
      sortDesc: false, // Default ascending order
      responseEntries: [],
      responseEntryLines: [],
      filters: {
        refID: "",
        dateRange: [null, null], // Initialize as an array with two null values
        debit: "",
        credit: "",
      },
    };
  },
  computed: {
    ...mapGetters("journalentryModule", {
      journalEntries: "journalentry",
      loadingEntries: "loading",
    }),
    ...mapGetters("journalentrylineModule", {
      journalEntryLines: "journalentryline",
      loadingLines: "loading",
    }),
    filteredJournalEntries() {
      return this.responseEntries.filter((entry) => {
        const matchesRefID =
          !this.filters.refID ||
          entry.refID.toLowerCase().includes(this.filters.refID.toLowerCase());
        const matchesDate =
          (!this.filters.dateRange[0] && !this.filters.dateRange[1]) || // Check if both range values are null
          (new Date(entry.date) >= new Date(this.filters.dateRange[0]) &&
            new Date(entry.date) <= new Date(this.filters.dateRange[1]));
        const matchesDebit =
          !this.filters.debit ||
          entry.totalDebit.toString().includes(this.filters.debit.toString());
        const matchesCredit =
          !this.filters.credit ||
          entry.totalCredit.toString().includes(this.filters.credit.toString());

        return matchesRefID && matchesDate && matchesDebit && matchesCredit;
      });
    },
  },
  methods: {
    ...mapActions("journalentryModule", [
      "getJournalEntryListAction",
      "getLinesByJournalEntryIDAction",
      "getJournalEntryLineListAction",
      "removeJournalEntryAction",
      "removeJournalEntryLineAction"
    ]),
    async deleteEntry(id) {
      let loader = customLoader('Please wait while journal entry is being removed');
      await this.removeJournalEntryAction(id)
      .then(async (response) => {
        loader.close();
        customSwal('Journal entry has been deleted')
        await this.loadJournalEntries();
        this.filteredJournalEntries;
      }).catch((error) => {
        loader.close();
        customError(error.response.data)
      });
    },
    formatDate(date) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
    viewJournalEntry(id) {
      return "/journalentry/view/" + id;
    },
    clearFilters() {
      this.filters = {
        refID: "",
        dateRange: [null, null], // Reset to null range
        debit: "",
        credit: "",
      };
    },
    async loadJournalEntries(){
      try {
      this.loading = true; // Set loading to true before fetching data

      // Fetch journal entries
      await this.getJournalEntryListAction();
      this.responseEntries = this.journalEntries;

      // Iterate over each journal entry
      this.responseEntries.forEach((entry) => {
        // Get lines for the current journal entry
        const lines = this.responseEntryLines.filter(
          (line) => line.journalEntryID === entry.id
        );

        // Calculate the total debit and credit for the current entry
        const debit = lines.reduce((sum, line) => sum + line.debit, 0);
        const credit = lines.reduce((sum, line) => sum + line.credit, 0);
        entry.totalDebit = entry.journalEntryLines.reduce((sum, line) => sum + line.debit, 0);
        entry.totalCredit = entry.journalEntryLines.reduce((sum, line) => sum + line.credit, 0);
      });
      
      this.totalRows = this.responseEntries.length;
      this.loading = false; // Set loading to false after fetching and merging data
    } catch (error) {
      console.error("Error fetching and merging entries:", error);
      this.loading = false; // Ensure loading is set to false in case of an error
    } finally {
      this.loading = false;
    }
    }
  },
  async mounted() {
    await this.loadJournalEntries();
  },
};
</script>
